import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  useCostRates,
  useSaveCostRates,
  useCostEstimate,
  useLlmCostEstimate,
  useTenantJourneyStages,
} from "../../lib/api";
import { STEP_DEFS } from "../../lib/journey";
import { FieldLabel, GhostButton, PrimaryButton, SecondaryButton, cardClass, inputClass } from "./primitives";

// Stage keys carry no label of their own on the cost payload. STEP_DEFS is
// only the platform default name for each stage — a brand can rename any of
// them on the Service Journey screen (e.g. Hyundai calls "jobcard" something
// other than "Job Card Created"), so the real label always comes from that
// brand's own saved overlay, falling back to the default only where the
// brand has never touched it.
const DEFAULT_STAGE_LABELS = Object.fromEntries(STEP_DEFS.map((s) => [s.key, s.label]));

function useStageLabels(tenantId) {
  const { data } = useTenantJourneyStages(tenantId);
  return useMemo(() => {
    const labels = { ...DEFAULT_STAGE_LABELS, _other: "Other sends" };
    for (const row of data?.rows || []) {
      if (row.stage_key && row.label) labels[row.stage_key] = row.label;
    }
    return labels;
  }, [data]);
}

const CURRENCY_SYMBOLS = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ" };
const symbolFor = (code) => CURRENCY_SYMBOLS[code] || `${code} `;

function money(value, currency) {
  const n = Number(value || 0);
  return `${symbolFor(currency)}${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function Loading() {
  return (
    <div className="mt-7 flex items-center gap-2 text-[13px] text-[#6B7280]">
      <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M12 3a9 9 0 100 18" />
      </svg>
      Loading
    </div>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div className={`${cardClass} p-4`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9CA3AF]">{label}</div>
      <div className="mt-1 text-[22px] font-semibold leading-7 text-[#111827]">{value}</div>
      {sub && <div className="mt-0.5 text-[12px] text-[#6B7280]">{sub}</div>}
    </div>
  );
}

function RateCard({ rates, measured, onSave, saving }) {
  const [form, setForm] = useState(rates);
  useEffect(() => setForm(rates), [rates]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const dirty = useMemo(
    () => Object.keys(rates).some((k) => String(rates[k]) !== String(form?.[k] ?? "")),
    [rates, form],
  );

  const canApplyMeasured = measured?.avg_joined != null || measured?.avg_invited != null;
  const applyMeasured = () =>
    setForm((f) => ({
      ...f,
      avg_group_size: measured.avg_joined ?? f.avg_group_size,
      avg_invited_count: measured.avg_invited ?? f.avg_invited_count,
    }));

  if (!form) return null;

  return (
    <div className={`${cardClass} p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h2 className="text-[20px] font-semibold leading-7 text-[#111827]">Rate card</h2>
        {canApplyMeasured && (
          <SecondaryButton type="button" onClick={applyMeasured}>
            Use this brand&rsquo;s measured group sizes
          </SecondaryButton>
        )}
      </div>
      <div className="my-[22px] h-px bg-[#F0F1F3]" />

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <FieldLabel>Currency</FieldLabel>
          <input className={`${inputClass} !w-[120px]`} value={form.currency} onChange={set("currency")} maxLength={8} />
        </div>
        <div>
          <FieldLabel>Template message rate</FieldLabel>
          <input type="number" step="0.01" min="0" className={`${inputClass} !w-[140px]`} value={form.template_rate} onChange={set("template_rate")} />
        </div>
        <div>
          <FieldLabel>Plain text / media rate</FieldLabel>
          <input type="number" step="0.01" min="0" className={`${inputClass} !w-[140px]`} value={form.direct_rate} onChange={set("direct_rate")} />
        </div>
        <div>
          <FieldLabel>Group size fallback</FieldLabel>
          <input type="number" step="0.1" min="1" max="8" className={`${inputClass} !w-[140px]`} value={form.avg_group_size} onChange={set("avg_group_size")} />
          <div className="mt-[6px] text-[12px] text-[#6B7280]">
            {measured?.avg_joined != null
              ? `Measured average: ${measured.avg_joined}. Used only when a group has no member count on file.`
              : "Used only when a group has no member count on file."}
          </div>
        </div>
        <div>
          <FieldLabel>Invite list fallback</FieldLabel>
          <input type="number" step="0.1" min="1" className={`${inputClass} !w-[140px]`} value={form.avg_invited_count} onChange={set("avg_invited_count")} />
          <div className="mt-[6px] text-[12px] text-[#6B7280]">
            {measured?.avg_invited != null
              ? `Measured average: ${measured.avg_invited}. Used only when a group has no invite record on file.`
              : "Used only when a group has no invite record on file."}
          </div>
        </div>
      </div>

      <div className="mt-[26px] flex items-center gap-3">
        <PrimaryButton onClick={() => onSave(form)} disabled={saving || !dirty}>
          {saving && (
            <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M12 3a9 9 0 100 18" />
            </svg>
          )}
          Save changes
        </PrimaryButton>
        {dirty && <GhostButton onClick={() => setForm(rates)}>Discard</GhostButton>}
      </div>
    </div>
  );
}

const isoDay = (date) => date.toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
};

// "Last 30 days" means the last 30 days including today, so the start is 29
// days back — off-by-one here would quietly widen every preset.
const PRESETS = [
  { key: "7", label: "Last 7 days", range: () => ({ from: daysAgo(6), to: isoDay(new Date()) }) },
  { key: "30", label: "Last 30 days", range: () => ({ from: daysAgo(29), to: isoDay(new Date()) }) },
  { key: "90", label: "Last 90 days", range: () => ({ from: daysAgo(89), to: isoDay(new Date()) }) },
  { key: "all", label: "All time", range: () => ({ from: "", to: "" }) },
];

function DateRangePicker({ value, onChange }) {
  const activePreset = PRESETS.find((p) => {
    const r = p.range();
    return r.from === value.from && r.to === value.to;
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => onChange(p.range())}
            className="h-[34px] cursor-pointer rounded-lg border border-[#D1D5DB] px-3 text-[13px] font-medium"
            style={{
              background: activePreset?.key === p.key ? "#2667B5" : "#FFFFFF",
              color: activePreset?.key === p.key ? "#FFFFFF" : "#4B5563",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-[6px]">
        <input
          type="date"
          className={`${inputClass} !h-[34px] !w-[152px] text-[13px]`}
          value={value.from}
          max={value.to || undefined}
          onChange={(e) => onChange({ ...value, from: e.target.value })}
        />
        <span className="text-[13px] text-[#6B7280]">to</span>
        <input
          type="date"
          className={`${inputClass} !h-[34px] !w-[152px] text-[13px]`}
          value={value.to}
          min={value.from || undefined}
          onChange={(e) => onChange({ ...value, to: e.target.value })}
        />
      </div>
    </div>
  );
}

function DailyTrend({ byDay, currency, countKey = "sends", countLabel = "send" }) {
  if (byDay.length < 2) return null;
  const peak = Math.max(...byDay.map((d) => d.cost));
  return (
    <div className="rounded-xl bg-[#F9FAFB] px-4 py-[14px]">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9CA3AF]">Daily spend</span>
        <span className="text-[12px] text-[#6B7280]">peak {money(peak, currency)}</span>
      </div>
      <div className="flex h-20 items-end gap-[3px]">
        {byDay.map((d) => {
          const count = d[countKey];
          return (
            <div
              key={d.date}
              title={`${d.date} · ${count} ${countLabel}${count === 1 ? "" : "s"} · ${money(d.cost, currency)}`}
              className="min-w-[3px] flex-1 rounded-sm bg-[#8FB8E6] hover:bg-[#2667B5]"
              style={{ height: `${peak ? Math.max(4, (d.cost / peak) * 100) : 4}%` }}
            />
          );
        })}
      </div>
      <div className="mt-[6px] flex justify-between text-[11px] text-[#9CA3AF]">
        <span>{byDay[0].date}</span>
        <span>{byDay[byDay.length - 1].date}</span>
      </div>
    </div>
  );
}

function SpendRow({ line, currency }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-white px-4 py-[14px] border border-[#EAECF0] shadow-[0_1px_2px_rgba(16,24,40,0.06),0_4px_12px_rgba(16,24,40,0.08)]">
      <div className="min-w-0">
        <div className="truncate text-[14px] font-medium text-[#111827]">{line.template}</div>
        <div className="mt-[2px] text-[12px] text-[#6B7280]">
          {line.sends} send{line.sends === 1 ? "" : "s"} · {line.messages} message{line.messages === 1 ? "" : "s"}
        </div>
      </div>
      <div className="shrink-0 text-[15px] font-semibold text-[#111827]">{money(line.cost, currency)}</div>
    </div>
  );
}

// The real spend already logged, grouped by which journey stage each
// template belongs to today — not a projection of what a stage will cost,
// just the already-real numbers read together.
function StageBreakdown({ byStage, currency, stageLabels }) {
  if (!byStage?.length) return null;
  const total = byStage.reduce((sum, l) => sum + l.cost, 0);
  const max = Math.max(...byStage.map((l) => l.cost), 0.01);

  return (
    <div className={`${cardClass} p-6`}>
      <h2 className="text-[20px] font-semibold leading-7 text-[#111827]">Spend by service stage</h2>
      <div className="my-[22px] h-px bg-[#F0F1F3]" />

      <div className="flex flex-col gap-3">
        {byStage.map((l) => {
          const pct = total ? Math.round((l.cost / total) * 100) : 0;
          return (
            <div key={l.stage}>
              <div className="mb-[6px] flex items-baseline justify-between gap-3">
                <span className="text-[13.5px] font-medium text-[#111827]">
                  {stageLabels[l.stage] || l.stage}
                </span>
                <span className="shrink-0 text-[13.5px] font-semibold text-[#111827]">
                  {money(l.cost, currency)} <span className="font-normal text-[#9CA3AF]">· {pct}%</span>
                </span>
              </div>
              <div className="h-[6px] overflow-hidden rounded-full bg-[#F0F1F3]">
                <div className="h-full rounded-full bg-[#2667B5]" style={{ width: `${(l.cost / max) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActualSpend({ actual, range, onRangeChange, loading }) {
  const currency = actual.currency;
  const hasPeriod = !!(range.from || range.to);
  return (
    <div className={`${cardClass} p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h2 className="flex items-center gap-2 text-[20px] font-semibold leading-7 text-[#111827]">
          WhatsApp spend
          {loading && (
            <svg className="animate-spin text-[#9CA3AF]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M12 3a9 9 0 100 18" />
            </svg>
          )}
        </h2>
        <DateRangePicker value={range} onChange={onRangeChange} />
      </div>
      <div className="my-[22px] h-px bg-[#F0F1F3]" />

      {!actual.lines.length ? (
        <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-[#BBD4F1] px-4 py-10 text-center">
          <div className="text-[14px] font-medium text-[#111827]">
            {hasPeriod ? "No template sends in this period" : "No template sends recorded yet"}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <StatTile label="Billable sends" value={actual.totals.billable_sends} />
            <StatTile label="Messages delivered" value={actual.totals.messages} />
            <StatTile
              label={hasPeriod ? "Spend in period" : "Spend to date"}
              value={money(actual.totals.cost, currency)}
              sub={
                actual.totals.first_send ? `${actual.totals.first_send} → ${actual.totals.last_send}` : undefined
              }
            />
          </div>

          <DailyTrend byDay={actual.by_day || []} currency={currency} />

          <div className="flex flex-col gap-2">
            {actual.lines.map((l) => (
              <SpendRow key={l.template} line={l} currency={currency} />
            ))}
          </div>

          {actual.totals.free_direct_sends > 0 && (
            <p className="text-[12px] text-[#6B7280]">
              Plus {actual.totals.free_direct_sends} plain-text send{actual.totals.free_direct_sends === 1 ? "" : "s"}{" "}
              that cost nothing.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// The only two classifiers apps/webhook/services/message_classifier.py has —
// a brand-new one needs a label added here too, same as the migration's
// check constraint.
const CLASSIFIER_LABELS = {
  classify_message: "Customer message classification",
  classify_staff_tone: "Staff tone monitoring",
};

function ClassifierRow({ line, currency }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-white px-4 py-[14px] border border-[#EAECF0] shadow-[0_1px_2px_rgba(16,24,40,0.06),0_4px_12px_rgba(16,24,40,0.08)]">
      <div className="min-w-0">
        <div className="truncate text-[14px] font-medium text-[#111827]">
          {CLASSIFIER_LABELS[line.classifier] || line.classifier}
        </div>
        <div className="mt-[2px] text-[12px] text-[#6B7280]">
          {line.calls} call{line.calls === 1 ? "" : "s"} · {line.tokens.toLocaleString("en-IN")} tokens
        </div>
      </div>
      <div className="shrink-0 text-[15px] font-semibold text-[#111827]">{money(line.cost, currency)}</div>
    </div>
  );
}

// Real spend on the LLM classifiers the app calls for every inbound WhatsApp
// message (apps/webhook/services/message_classifier.py) — its own date-range
// filter, deliberately separate from the WhatsApp section's, since a Super
// Admin comparing "did AI usage spike this week" wants to pick its own window
// independently of the messaging one.
function LlmSpend({ actual, range, onRangeChange, loading }) {
  const currency = actual.currency;
  const hasPeriod = !!(range.from || range.to);
  return (
    <div className={`${cardClass} p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h2 className="flex items-center gap-2 text-[20px] font-semibold leading-7 text-[#111827]">
          LLM classification spend
          {loading && (
            <svg className="animate-spin text-[#9CA3AF]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M12 3a9 9 0 100 18" />
            </svg>
          )}
        </h2>
        <DateRangePicker value={range} onChange={onRangeChange} />
      </div>
      <div className="my-[22px] h-px bg-[#F0F1F3]" />

      {!actual.by_classifier.length ? (
        <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-[#BBD4F1] px-4 py-10 text-center">
          <div className="text-[14px] font-medium text-[#111827]">
            {hasPeriod ? "No classifier calls in this period" : "No classifier calls recorded yet"}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <StatTile label="Classifier calls" value={actual.totals.calls} />
            <StatTile label="Tokens processed" value={actual.totals.tokens.toLocaleString("en-IN")} />
            <StatTile
              label={hasPeriod ? "Spend in period" : "Spend to date"}
              value={money(actual.totals.cost, currency)}
              sub={
                actual.totals.first_call ? `${actual.totals.first_call} → ${actual.totals.last_call}` : undefined
              }
            />
          </div>

          <DailyTrend byDay={actual.by_day || []} currency={currency} countKey="calls" countLabel="call" />

          <div className="flex flex-col gap-2">
            {actual.by_classifier.map((l) => (
              <ClassifierRow key={l.classifier} line={l} currency={currency} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CostEditor({ tenantId }) {
  // Default to all time: a brand with a handful of sends spread over months
  // would otherwise open on an empty chart and look broken.
  const [range, setRange] = useState({ from: "", to: "" });
  // Its own range, independent of the WhatsApp section's — a Super Admin
  // comparing "did AI usage spike this week" wants to pick its own window.
  const [llmRange, setLlmRange] = useState({ from: "", to: "" });
  const { data: rateData, isLoading: ratesLoading, error: ratesError } = useCostRates(tenantId);
  const {
    data: estimate,
    isLoading: estimateLoading,
    isFetching,
    error: estimateError,
  } = useCostEstimate(tenantId, range);
  const {
    data: llmEstimate,
    isFetching: llmFetching,
  } = useLlmCostEstimate(tenantId, llmRange);
  const save = useSaveCostRates(tenantId);
  const stageLabels = useStageLabels(tenantId);

  if (ratesLoading || estimateLoading) return <Loading />;

  // A failed request leaves isLoading false with no data, so this has to be a
  // separate branch — falling back to the loading branch would spin forever.
  const error = ratesError || estimateError;
  if (error || !rateData || !estimate) {
    return (
      <div className="mt-7 rounded-xl bg-[#FEF2F2] px-4 py-[14px] text-[13px] text-[#B91C1C]">
        {error?.response?.data?.detail || error?.message || "The cost endpoints returned no data."}
      </div>
    );
  }

  const onSave = async (form) => {
    try {
      await save.mutateAsync({
        currency: String(form.currency || "INR"),
        template_rate: Number(form.template_rate),
        direct_rate: Number(form.direct_rate),
        avg_group_size: Number(form.avg_group_size),
        avg_invited_count: Number(form.avg_invited_count),
        notes: form.notes || "",
      });
      toast.success("Rate card saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const { actual } = estimate;

  return (
    <div className="mt-7 flex flex-col gap-6">
      <RateCard rates={rateData.rates} measured={rateData.measured} onSave={onSave} saving={save.isPending} />
      <StageBreakdown byStage={actual.by_stage} currency={actual.currency} stageLabels={stageLabels} />
      <ActualSpend actual={actual} range={range} onRangeChange={setRange} loading={isFetching} />
      {llmEstimate?.actual && (
        <LlmSpend
          actual={llmEstimate.actual}
          range={llmRange}
          onRangeChange={setLlmRange}
          loading={llmFetching}
        />
      )}
    </div>
  );
}
